/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import type {
  AccountMeta,
  AccountSignerMeta,
  Address,
  FixedSizeCodec,
  FixedSizeDecoder,
  FixedSizeEncoder,
  Instruction,
  InstructionWithAccounts,
  InstructionWithData,
  ReadonlyAccount,
  ReadonlySignerAccount,
  ReadonlyUint8Array,
  TransactionSigner,
  WritableAccount,
} from "@solana/kit";
import type { ResolvedInstructionAccount } from "@solana/program-client-core";
import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU16Decoder,
  getU16Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
} from "@solana/kit";
import { getAccountMetaFactory } from "@solana/program-client-core";
import { WHIRLPOOL_PROGRAM_ADDRESS } from "../programs/index.js";

export const SET_PROTOCOL_FEE_RATE_DISCRIMINATOR: ReadonlyUint8Array =
  new Uint8Array([95, 7, 4, 50, 154, 79, 156, 131]);

export function getSetProtocolFeeRateDiscriminatorBytes(): ReadonlyUint8Array {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    SET_PROTOCOL_FEE_RATE_DISCRIMINATOR,
  );
}

export type SetProtocolFeeRateInstruction<
  TProgram extends string = typeof WHIRLPOOL_PROGRAM_ADDRESS,
  TAccountWhirlpoolsConfig extends string | AccountMeta = string,
  TAccountWhirlpool extends string | AccountMeta = string,
  TAccountFeeAuthority extends string | AccountMeta = string,
  TRemainingAccounts extends readonly AccountMeta[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountWhirlpoolsConfig extends string
        ? ReadonlyAccount<TAccountWhirlpoolsConfig>
        : TAccountWhirlpoolsConfig,
      TAccountWhirlpool extends string
        ? WritableAccount<TAccountWhirlpool>
        : TAccountWhirlpool,
      TAccountFeeAuthority extends string
        ? ReadonlySignerAccount<TAccountFeeAuthority> &
            AccountSignerMeta<TAccountFeeAuthority>
        : TAccountFeeAuthority,
      ...TRemainingAccounts,
    ]
  >;

export interface SetProtocolFeeRateInstructionData {
  discriminator: ReadonlyUint8Array;
  protocolFeeRate: number;
}

export interface SetProtocolFeeRateInstructionDataArgs {
  protocolFeeRate: number;
}

export function getSetProtocolFeeRateInstructionDataEncoder(): FixedSizeEncoder<SetProtocolFeeRateInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["protocolFeeRate", getU16Encoder()],
    ]),
    (value) => ({
      ...value,
      discriminator: SET_PROTOCOL_FEE_RATE_DISCRIMINATOR,
    }),
  );
}

export function getSetProtocolFeeRateInstructionDataDecoder(): FixedSizeDecoder<SetProtocolFeeRateInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["protocolFeeRate", getU16Decoder()],
  ]);
}

export function getSetProtocolFeeRateInstructionDataCodec(): FixedSizeCodec<
  SetProtocolFeeRateInstructionDataArgs,
  SetProtocolFeeRateInstructionData
> {
  return combineCodec(
    getSetProtocolFeeRateInstructionDataEncoder(),
    getSetProtocolFeeRateInstructionDataDecoder(),
  );
}

export interface SetProtocolFeeRateInput<
  TAccountWhirlpoolsConfig extends string = string,
  TAccountWhirlpool extends string = string,
  TAccountFeeAuthority extends string = string,
> {
  whirlpoolsConfig: Address<TAccountWhirlpoolsConfig>;
  whirlpool: Address<TAccountWhirlpool>;
  feeAuthority: TransactionSigner<TAccountFeeAuthority>;
  protocolFeeRate: SetProtocolFeeRateInstructionDataArgs["protocolFeeRate"];
}

export function getSetProtocolFeeRateInstruction<
  TAccountWhirlpoolsConfig extends string,
  TAccountWhirlpool extends string,
  TAccountFeeAuthority extends string,
  TProgramAddress extends Address = typeof WHIRLPOOL_PROGRAM_ADDRESS,
>(
  input: SetProtocolFeeRateInput<
    TAccountWhirlpoolsConfig,
    TAccountWhirlpool,
    TAccountFeeAuthority
  >,
  config?: { programAddress?: TProgramAddress },
): SetProtocolFeeRateInstruction<
  TProgramAddress,
  TAccountWhirlpoolsConfig,
  TAccountWhirlpool,
  TAccountFeeAuthority
> {
  // Program address.
  const programAddress = config?.programAddress ?? WHIRLPOOL_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    whirlpoolsConfig: {
      value: input.whirlpoolsConfig ?? null,
      isWritable: false,
    },
    whirlpool: { value: input.whirlpool ?? null, isWritable: true },
    feeAuthority: { value: input.feeAuthority ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta("whirlpoolsConfig", accounts.whirlpoolsConfig),
      getAccountMeta("whirlpool", accounts.whirlpool),
      getAccountMeta("feeAuthority", accounts.feeAuthority),
    ],
    data: getSetProtocolFeeRateInstructionDataEncoder().encode(
      args as SetProtocolFeeRateInstructionDataArgs,
    ),
    programAddress,
  } as SetProtocolFeeRateInstruction<
    TProgramAddress,
    TAccountWhirlpoolsConfig,
    TAccountWhirlpool,
    TAccountFeeAuthority
  >);
}

export interface ParsedSetProtocolFeeRateInstruction<
  TProgram extends string = typeof WHIRLPOOL_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> {
  programAddress: Address<TProgram>;
  accounts: {
    whirlpoolsConfig: TAccountMetas[0];
    whirlpool: TAccountMetas[1];
    feeAuthority: TAccountMetas[2];
  };
  data: SetProtocolFeeRateInstructionData;
}

export function parseSetProtocolFeeRateInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedSetProtocolFeeRateInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 3) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 3,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      whirlpoolsConfig: getNextAccount(),
      whirlpool: getNextAccount(),
      feeAuthority: getNextAccount(),
    },
    data: getSetProtocolFeeRateInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
